import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { QueryClient, type Mutation } from '@tanstack/react-query';
import type { Persister } from '@tanstack/react-query-persist-client';
import { generateId } from '@split/core';
import type {
  CreateExpenseRequest,
  CreateSettlementRequest,
  UpdateExpenseRequest,
} from '@split/shared';
import { del, get, set } from 'idb-keyval';
import { clearRecoveryPhrase } from '../components/auth/recoveryStore';
import {
  clearQueuedReceipts,
  clearReceiptCache,
  dropQueuedReceipt,
  evictReceipt,
  queuedReceipt,
  queuedReceiptIds,
} from '../lib/receipts';
import { clearNotices } from '../lib/notices';
import { api, isApiError } from './http';
import { mk, qk } from './keys';

const DAY = 24 * 60 * 60 * 1000;

export interface CreateExpenseVars {
  groupId: string;
  body: CreateExpenseRequest & { id: string };
  /**
   * A receipt photo was queued with `queueReceipt(body.id)` (variables are persisted as JSON,
   * so the Blob itself lives in IndexedDB); it is uploaded once the expense exists.
   */
  hasReceipt?: boolean;
}
export interface UpdateExpenseVars {
  expenseId: string;
  groupId: string;
  body: UpdateExpenseRequest;
  /** Like `CreateExpenseVars.hasReceipt`. */
  hasReceipt?: boolean;
  removeReceipt?: boolean;
  /** Edits queued offline: see `chainQueuedEdit`. */
  queueId?: string;
  after?: string;
}
export interface UploadReceiptVars {
  expenseId: string;
  /** `null` when re-queued by `resumeQueuedReceipts` (only used to refresh the group). */
  groupId: string | null;
  /** The expense's title, to tell the user which photo was rejected (`null` if unknown). */
  title?: string | null;
}

/**
 * Edits, receipt uploads and deletes of one expense share a mutation scope, so queued ones are
 * replayed one after the other, in order (TanStack Query runs a scope serially).
 */
export function expenseScope(expenseId: string) {
  return { id: `expense:${expenseId}` };
}
export interface DeleteExpenseVars {
  expenseId: string;
  groupId: string;
  /** To name the expense if the delete fails in the background. */
  title?: string;
}
export interface CreateSettlementVars {
  groupId: string;
  body: CreateSettlementRequest & { id: string };
}
export interface SettlementActionVars {
  settlementId: string;
  notificationId?: string | null;
  groupId?: string | null;
}

function isClientError(error: unknown): boolean {
  return isApiError(error) && error.status >= 400 && error.status < 500;
}

/**
 * A 4xx that retrying the same request cannot fix. A lapsed session, missing exchange rates and
 * the rate limit can recover, so those are not final.
 */
export function isFinalRejection(error: unknown): boolean {
  return (
    isClientError(error) &&
    !isApiError(error, 'UNAUTHENTICATED') &&
    !isApiError(error, 'FX_UNAVAILABLE') &&
    !isApiError(error, 'RATE_LIMITED')
  );
}

function retry(failureCount: number, error: unknown): boolean {
  if (isClientError(error)) return false;
  return failureCount < 2;
}

export function createQueryClient(): QueryClient {
  const client = new QueryClient({
    defaultOptions: {
      queries: {
        gcTime: 7 * DAY,
        staleTime: 30_000,
        networkMode: 'offlineFirst',
        retry,
        refetchOnWindowFocus: true,
      },
      mutations: { networkMode: 'online', retry: false },
    },
  });
  registerMutationDefaults(client);
  return client;
}

async function uploadReceipt(expenseId: string, receipt: Blob) {
  await api('uploadReceipt', {
    params: { id: expenseId },
    raw: { data: receipt, contentType: receipt.type || 'image/jpeg' },
  });
}

/** Queues the upload of the photo kept by `queueReceipt` as its own (offline-capable) mutation. */
function queueReceiptUpload(client: QueryClient, vars: UploadReceiptVars) {
  void client
    .getMutationCache()
    .build(client, { mutationKey: mk.uploadReceipt, scope: expenseScope(vars.expenseId) })
    .execute(vars)
    .catch(() => undefined);
}

/**
 * A create, or an edit queued offline, that failed in a way retrying may fix: it stays in the
 * cache (and is persisted) with its photo, for the Retry / Discard of the feed and the detail.
 */
export function awaitsRetry(m: Mutation): boolean {
  if (m.state.status !== 'error' || isFinalRejection(m.state.error)) return false;
  const key = m.options.mutationKey?.[0];
  if (key === mk.createExpense[0]) return true;
  return key === mk.updateExpense[0] && m.meta?.reportConflict === true;
}

/** Which mutations are persisted with the cache: queued ones and failed ones awaiting Retry. */
export function shouldPersistMutation(m: Mutation): boolean {
  return m.state.isPaused || awaitsRetry(m);
}

/**
 * Whether a mutation other than the one with `except` variables still needs the photo queued
 * for `expenseId`: a create/edit with `hasReceipt` not yet sent (or failed and awaiting Retry)
 * and, with `uploads`, an upload.
 */
export function receiptStillQueued(
  client: QueryClient,
  expenseId: string,
  except: unknown,
  uploads: boolean,
): boolean {
  return client
    .getMutationCache()
    .getAll()
    .some((m) => {
      if (m.state.variables === except) return false;
      if (m.state.status !== 'pending' && !awaitsRetry(m)) return false;
      const key = m.options.mutationKey?.[0];
      if (key === mk.uploadReceipt[0]) {
        return uploads && (m.state.variables as UploadReceiptVars).expenseId === expenseId;
      }
      if (key === mk.createExpense[0]) {
        const vars = m.state.variables as CreateExpenseVars;
        return vars.hasReceipt === true && vars.body.id === expenseId;
      }
      if (key === mk.updateExpense[0]) {
        const vars = m.state.variables as UpdateExpenseVars;
        return vars.hasReceipt === true && vars.expenseId === expenseId;
      }
      return false;
    });
}

/**
 * Re-queues the upload of receipt photos no queued mutation refers to any more (an upload that
 * failed on a server or network error keeps its photo). Run once the persisted cache is restored.
 */
export async function resumeQueuedReceipts(client: QueryClient): Promise<void> {
  for (const expenseId of await queuedReceiptIds()) {
    if (!receiptStillQueued(client, expenseId, undefined, true)) {
      queueReceiptUpload(client, {
        expenseId,
        groupId: null,
        title: cachedTitle(client, expenseId),
      });
    }
  }
}

function cachedTitle(client: QueryClient, expenseId: string): string | null {
  return (
    client.getQueryData<{ expense: { title: string } }>(qk.expense(expenseId))?.expense.title ??
    null
  );
}

// ---------------------------------------------------------------------------------------------
// Chained offline edits. Edits queued offline were made on top of the previous queued edit of
// the same expense, so they carry the same `expectedVersion`: once one succeeds, the next one
// expects its result. Only the edit made on top of it (`after`) does: a Retry of an earlier
// failed edit, an online save or another tab still sends its own version, so a newer edit is
// never overwritten without a conflict. The results are kept in localStorage so a reload between
// two queued edits (they are persisted with the cache) does not turn the next one into a false
// conflict.
// ---------------------------------------------------------------------------------------------

const VERSIONS_KEY = 'split.editVersions';
type VersionChains = Record<string, { version: number; at: number }>;
/** Fallback when localStorage is unavailable (the chain then lasts until a reload). */
const memoryChains = new Map<string, { version: number; at: number }>();

/**
 * `queueId` / `after` for an edit of `expenseId` queued now: it was made on top of the optimistic
 * state of the last queued edit of it that has not run yet, if any.
 */
export function chainQueuedEdit(
  client: QueryClient,
  expenseId: string,
): Pick<UpdateExpenseVars, 'queueId' | 'after'> {
  const previous = client
    .getMutationCache()
    .findAll({ mutationKey: mk.updateExpense, status: 'pending' })
    .map((m) => m.state.variables as UpdateExpenseVars | undefined)
    .filter((v) => v?.expenseId === expenseId)
    .at(-1);
  return previous?.queueId === undefined
    ? { queueId: generateId() }
    : { queueId: generateId(), after: previous.queueId };
}

function readChains(): VersionChains {
  let stored: VersionChains = {};
  try {
    const raw = localStorage.getItem(VERSIONS_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (parsed && typeof parsed === 'object') stored = parsed as VersionChains;
  } catch {
    // ignore
  }
  // Also when only writing failed (e.g. storage full): those chains are only in memory.
  return { ...stored, ...Object.fromEntries(memoryChains) };
}

function ownVersion(chain: string): number | undefined {
  return readChains()[chain]?.version;
}

function rememberOwnVersion(chain: string, version: number): void {
  const now = Date.now();
  // Queued mutations are dropped after `PERSIST_MAX_AGE`, so older chains can go too.
  const chains: VersionChains = Object.fromEntries(
    Object.entries(readChains()).filter(([, v]) => now - v.at <= PERSIST_MAX_AGE),
  );
  chains[chain] = { version, at: now };
  try {
    localStorage.setItem(VERSIONS_KEY, JSON.stringify(chains));
  } catch {
    memoryChains.set(chain, { version, at: now });
  }
}

function clearVersionChains(): void {
  memoryChains.clear();
  try {
    localStorage.removeItem(VERSIONS_KEY);
  } catch {
    // ignore
  }
}

/**
 * A retried confirm / reject whose first attempt reached the server (only the answer was lost)
 * is told the payment was already resolved: when it already has the wanted status, that is
 * success (so e.g. Undo still calls `undo-reject`).
 */
function alreadyResolved(status: 'confirmed' | 'disputed') {
  return (e: unknown): null => {
    if (
      isApiError(e, 'CONFLICT') &&
      (e.details as { status?: unknown } | undefined)?.status === status
    )
      return null;
    throw e;
  };
}

/**
 * Default mutation functions for everything that may be queued offline: paused mutations are
 * persisted with the cache and resumed after a reload, which only works with defaults keyed by
 * `mutationKey` (TanStack Query docs, "Persisting Offline mutations").
 */
export function registerMutationDefaults(client: QueryClient): void {
  const invalidateGroup = (groupId: string | null | undefined) => {
    void client.invalidateQueries({ queryKey: qk.overview });
    void client.invalidateQueries({ queryKey: qk.groups });
    void client.invalidateQueries({ queryKey: qk.people });
    void client.invalidateQueries({ queryKey: ['activity'] });
    if (groupId) {
      void client.invalidateQueries({ queryKey: qk.groupFeed(groupId) });
      void client.invalidateQueries({ queryKey: qk.groupBalances(groupId) });
    }
  };
  const invalidateInbox = () => {
    void client.invalidateQueries({ queryKey: qk.notifications });
    void client.invalidateQueries({ queryKey: qk.unreadCount });
  };

  // The receipt is a separate mutation: a failed upload never marks the expense as failed.
  client.setMutationDefaults(mk.createExpense, {
    networkMode: 'offlineFirst',
    retry,
    // A failed create stays (and is persisted) for the feed's Retry / Discard.
    gcTime: Infinity,
    mutationFn: async ({ groupId, body, hasReceipt }: CreateExpenseVars) => {
      const res = await api('createExpense', { params: { id: groupId }, body });
      if (hasReceipt === true)
        queueReceiptUpload(client, { expenseId: body.id, groupId, title: body.title ?? null });
      return res;
    },
    onError: (e, vars: CreateExpenseVars) => {
      // Rejected by the API: the expense will never exist, so its photo has nowhere to go. After
      // a server or network error (or a lapsed session, or exchange rates being unavailable for
      // now) the feed's Retry still needs it, until it succeeds or is discarded.
      if (
        vars.hasReceipt === true &&
        isFinalRejection(e) &&
        !receiptStillQueued(client, vars.body.id, vars, true)
      )
        void dropQueuedReceipt(vars.body.id);
    },
    onSettled: (_d, _e, vars: CreateExpenseVars) => invalidateGroup(vars.groupId),
  });
  // A conflict (someone else edited it meanwhile) stops the chain instead of overwriting.
  client.setMutationDefaults(mk.updateExpense, {
    networkMode: 'offlineFirst',
    retry,
    // A failed queued edit stays (and is persisted) for its Retry / Discard.
    gcTime: Infinity,
    mutationFn: async (vars: UpdateExpenseVars) => {
      const { expenseId, groupId, body, hasReceipt, removeReceipt } = vars;
      const { queueId, after } = vars;
      const expectedVersion =
        (after !== undefined ? ownVersion(after) : undefined) ?? body.expectedVersion;
      const res = await api('updateExpense', {
        params: { id: expenseId },
        body: { ...body, expectedVersion },
      });
      if (queueId !== undefined) rememberOwnVersion(queueId, res.expense.version);
      client.setQueryData(qk.expense(expenseId), (old: { history: unknown[] } | undefined) =>
        old ? { ...old, expense: res.expense } : old,
      );
      if (removeReceipt) {
        await api('deleteReceipt', { params: { id: expenseId } });
        void evictReceipt(expenseId);
        // A photo still queued by an earlier edit must not come back after this removal (its
        // upload runs after this edit); one for a later queued edit is kept.
        if (!receiptStillQueued(client, expenseId, vars, false)) await dropQueuedReceipt(expenseId);
      }
      if (hasReceipt === true)
        queueReceiptUpload(client, { expenseId, groupId, title: res.expense.title });
      return res;
    },
    onError: (e, vars: UpdateExpenseVars, _r, context) => {
      // A queued edit keeps its photo for Retry unless the API rejected it for good. The form
      // (not queued) still holds the photo and queues it again on the next Save.
      const keep = context.meta?.reportConflict === true && !isFinalRejection(e);
      if (
        vars.hasReceipt === true &&
        !keep &&
        !receiptStillQueued(client, vars.expenseId, vars, true)
      )
        void dropQueuedReceipt(vars.expenseId);
    },
    onSettled: (_d, _e, vars: UpdateExpenseVars) => {
      void client.invalidateQueries({ queryKey: qk.expense(vars.expenseId) });
      invalidateGroup(vars.groupId);
    },
  });
  client.setMutationDefaults(mk.uploadReceipt, {
    networkMode: 'offlineFirst',
    retry,
    mutationFn: async ({ expenseId }: UploadReceiptVars) => {
      const blob = await queuedReceipt(expenseId);
      if (blob) await uploadReceipt(expenseId, blob);
      await dropQueuedReceipt(expenseId);
      await evictReceipt(expenseId);
    },
    onError: (e, vars: UploadReceiptVars) => {
      // Rejected by the API (type, size, gone): retrying cannot help (`SessionEffects` tells the
      // user). On a server or network error (or a lapsed session) the photo is kept and
      // `resumeQueuedReceipts` tries again on the next start.
      if (isFinalRejection(e)) void dropQueuedReceipt(vars.expenseId);
    },
    onSettled: (_d, _e, vars: UploadReceiptVars) => {
      void client.invalidateQueries({ queryKey: qk.expense(vars.expenseId) });
      invalidateGroup(vars.groupId);
    },
  });
  client.setMutationDefaults(mk.deleteExpense, {
    networkMode: 'offlineFirst',
    retry,
    mutationFn: ({ expenseId }: DeleteExpenseVars) =>
      api('deleteExpense', { params: { id: expenseId } }),
    onSettled: (_d, _e, vars: DeleteExpenseVars) => {
      void client.invalidateQueries({ queryKey: qk.expense(vars.expenseId) });
      invalidateGroup(vars.groupId);
    },
  });
  client.setMutationDefaults(mk.createSettlement, {
    networkMode: 'offlineFirst',
    retry,
    mutationFn: ({ groupId, body }: CreateSettlementVars) =>
      api('createSettlement', { params: { id: groupId }, body }),
    onSettled: (_d, _e, vars: CreateSettlementVars) => invalidateGroup(vars.groupId),
  });
  client.setMutationDefaults(mk.confirmSettlement, {
    networkMode: 'offlineFirst',
    retry,
    mutationFn: async ({ settlementId, notificationId }: SettlementActionVars) => {
      const res = await api('confirmSettlement', { params: { id: settlementId } }).catch(
        alreadyResolved('confirmed'),
      );
      if (notificationId) await api('markNotificationRead', { params: { id: notificationId } });
      return res;
    },
    onSettled: (_d, _e, vars: SettlementActionVars) => {
      invalidateInbox();
      invalidateGroup(vars.groupId);
    },
  });
  client.setMutationDefaults(mk.rejectSettlement, {
    networkMode: 'offlineFirst',
    retry,
    mutationFn: async ({ settlementId, notificationId }: SettlementActionVars) => {
      const res = await api('rejectSettlement', { params: { id: settlementId } }).catch(
        alreadyResolved('disputed'),
      );
      if (notificationId) await api('markNotificationRead', { params: { id: notificationId } });
      return res;
    },
    onSettled: (_d, _e, vars: SettlementActionVars) => {
      invalidateInbox();
      invalidateGroup(vars.groupId);
    },
  });
  client.setMutationDefaults(mk.markRead, {
    networkMode: 'offlineFirst',
    retry,
    mutationFn: ({ id }: { id: string }) => api('markNotificationRead', { params: { id } }),
    onSettled: invalidateInbox,
  });
  client.setMutationDefaults(mk.markAllRead, {
    networkMode: 'offlineFirst',
    retry,
    mutationFn: () => api('markAllNotificationsRead'),
    onSettled: invalidateInbox,
  });
}

const PERSIST_KEY = 'split.queryCache';

/**
 * Forgets everything the previous user left on this device: cached queries, queued mutations
 * (and their receipt photos), the persisted cache, cached receipt images and the recovery code.
 */
export function resetUserState(client: QueryClient): void {
  clearRecoveryPhrase();
  clearVersionChains();
  clearNotices();
  client.clear();
  void clearQueuedReceipts();
  void clearReceiptCache();
  void (async () => {
    try {
      await del(PERSIST_KEY);
    } catch {
      // No IndexedDB: nothing was persisted.
    }
  })();
}

const LAST_USER_KEY = 'split.lastUserId';
/** Who this tab's client holds data for (the stored id is shared by all tabs). */
const clientUsers = new WeakMap<QueryClient, string>();

/**
 * Records who is signed in on this device; when it is not who was signed in last, here or in
 * this tab (a 401 or a disabled account ends a session without "Sign out"), the previous user's
 * state is reset first. Returns whether it was.
 */
export function noteSignedInUser(client: QueryClient, userId: string): boolean {
  let last: string | null = null;
  try {
    last = localStorage.getItem(LAST_USER_KEY);
    localStorage.setItem(LAST_USER_KEY, userId);
  } catch {
    // Storage unavailable.
  }
  const mine = clientUsers.get(client);
  clientUsers.set(client, userId);
  if ((last === null || last === userId) && (mine === undefined || mine === userId)) return false;
  resetUserState(client);
  return true;
}

/**
 * Another tab signed in as someone else: drop this tab's copy of the previous user's cache and
 * queued mutations at once, before it persists them back or replays them with the new session
 * (that tab already reset the shared storage). Returns the unsubscribe function.
 */
export function watchOtherTabsSignIn(client: QueryClient): () => void {
  const onStorage = (e: StorageEvent) => {
    if (e.key !== LAST_USER_KEY || e.newValue === null) return;
    const mine = clientUsers.get(client);
    if (mine === undefined || mine === e.newValue) return;
    clientUsers.set(client, e.newValue);
    client.clear();
  };
  window.addEventListener('storage', onStorage);
  return () => window.removeEventListener('storage', onStorage);
}

// ---------------------------------------------------------------------------------------------
// Signing out. The session cookie is shared by every tab, so signing out in one ends it in all of
// them: the others are told at once (BroadcastChannel; the removal of `LAST_USER_KEY` fires a
// `storage` event as a fallback) and forget the user's data the same way.
// ---------------------------------------------------------------------------------------------

const SESSION_CHANNEL = 'split.session';
interface SessionMessage {
  type: 'signed-out';
}
let channel: BroadcastChannel | null | undefined;

/** This tab's channel (one per tab: a channel never receives its own messages). */
function sessionChannel(): BroadcastChannel | null {
  if (channel === undefined) {
    try {
      channel =
        typeof BroadcastChannel === 'function' ? new BroadcastChannel(SESSION_CHANNEL) : null;
      // Node (tests) would otherwise keep the process alive.
      (channel as { unref?: () => void } | null)?.unref?.();
    } catch {
      channel = null;
    }
  }
  return channel;
}

/** Forgets the signed-in user in this tab: their data, and the session (signed-out screens). */
function endSession(client: QueryClient): void {
  clientUsers.delete(client);
  resetUserState(client);
  client.setQueryData(qk.session, null);
}

/** After "Sign out": forgets the user on this device and signs out the other open tabs. */
export function signOutEverywhere(client: QueryClient): void {
  try {
    localStorage.removeItem(LAST_USER_KEY);
  } catch {
    // Storage unavailable.
  }
  endSession(client);
  try {
    sessionChannel()?.postMessage({ type: 'signed-out' } satisfies SessionMessage);
  } catch {
    // Channel closed.
  }
}

/**
 * Another tab signed out: forget the user here too and call `onSignedOut` (to show the signed-out
 * screen). Only a tab that was signed in reacts. Returns the unsubscribe function.
 */
export function watchOtherTabsSignOut(client: QueryClient, onSignedOut: () => void): () => void {
  const handle = () => {
    if (clientUsers.get(client) === undefined && !client.getQueryData(qk.session)) return;
    onSignedOut();
    endSession(client);
  };
  const onStorage = (e: StorageEvent) => {
    if (e.key === LAST_USER_KEY && e.newValue === null) handle();
  };
  const onMessage = (e: MessageEvent) => {
    if ((e.data as Partial<SessionMessage> | null)?.type === 'signed-out') handle();
  };
  const ch = sessionChannel();
  window.addEventListener('storage', onStorage);
  ch?.addEventListener('message', onMessage);
  return () => {
    window.removeEventListener('storage', onStorage);
    ch?.removeEventListener('message', onMessage);
  };
}

/**
 * A 401 in a tab that missed the sign-out of another one (asleep, or no BroadcastChannel nor
 * `storage` event): when this device no longer records this tab's user as signed in, they signed
 * out (or someone else signed in) elsewhere, so their data goes. A 401 alone (session expired,
 * revoked from another device) keeps it: the same user signing in again finds their queued
 * changes. Returns whether the session was ended.
 */
export function endSessionIfSignedOutElsewhere(client: QueryClient): boolean {
  const mine = clientUsers.get(client);
  if (mine === undefined) return false;
  let last: string | null = mine;
  try {
    last = localStorage.getItem(LAST_USER_KEY);
  } catch {
    // Storage unavailable: nothing to compare with.
  }
  if (last === mine) return false;
  endSession(client);
  return true;
}

/**
 * IndexedDB persister for the query cache (offline app shell shows cached data). Cached queries
 * persisted by another app version (`buster`) are dropped, but queued mutations are kept: they
 * are the user's unsent changes, and their variables follow the API contract, not the cache's.
 */
export function createPersister(buster: string): Persister {
  const persister = createAsyncStoragePersister({
    storage: {
      getItem: (key: string) => get<string>(key).then((v) => v ?? null),
      setItem: (key: string, value: string) => set(key, value),
      removeItem: (key: string) => del(key),
    },
    key: PERSIST_KEY,
    throttleTime: 1000,
  });
  return {
    ...persister,
    restoreClient: async () => {
      const persisted = await persister.restoreClient();
      if (!persisted || persisted.buster === buster) return persisted;
      return { ...persisted, buster, clientState: { ...persisted.clientState, queries: [] } };
    },
  };
}

export const PERSIST_MAX_AGE = 7 * DAY;
